// What the machine will show, from what the wizard holds.
//
// The Final Production Review listed each product's executions in its own little
// table. That is the wizard's own shape, and it is not the shape the floor sees:
// the operator panel shows ONE list per machine, in the order it will be run,
// with merged drums collapsed into a single row and the products of a lot named
// once at the top — `CYYF 4x2.5 + CYYF 3x2.5`, a group-by colour launch being
// exactly the case where the two views diverge most.
//
// ── why this is an adapter and not a renderer ──────────────────────────────
//
// The grouping is NOT re-implemented here. The queue page and the operator panel
// both turn a queue into rows with the same three steps (flatten → merge blocks
// → lot), and that lives in `machineQueueBlocks.js`. A third implementation
// against a third input shape is exactly how this codebase got a bedding rule in
// three places that disagreed by 47%.
//
// So this builds the `groups` shape `buildBlockRows` already consumes, out of
// wizard state, and the caller renders the result. One grouping rule, two
// sources.
//
// ── the three things that have to be predicted ─────────────────────────────
//
// 1. WHICH EXECUTIONS SHARE A DRUM — `buildMergedOutputKey` (what makes two
//    comparable) plus `planMergeSlots` (which of those actually pair), both in
//    `helpers/mergeSlot.js`, duplicated byte-for-byte from the backend. Merged
//    executions are given the SAME synthetic token id, which is what
//    `sameSharedExecution` keys on, so they collapse into one block here exactly
//    as they will on the machine.
//
// 2. WHAT ORDER THEY REACH THE MACHINE IN — `helpers/groupBy.js`, likewise a
//    byte-for-byte copy. GROUP BY is not a merge: the drums stay separate
//    tokens, they are only enqueued so that everything of one colour (or one
//    wire Ø) runs back to back. A group-by-colour launch of a CYYF 3x2.5 and a
//    CYYF 4x2.5 is blue+blue, brown+brown, green-yellow+green-yellow and the
//    4x2.5's black alone — NOT three drums of one product then four of the
//    other, which is what this preview showed until the `groupByPhases` flag
//    reached it.
//
// 3. WHAT THE OPERATOR WILL SEE IN THE INPUT COLUMN — the wizard has no
//    `materialPlan` (the launch computes it), so the copper and compound lines
//    are derived from the standard here. Display only: no kilograms, because
//    those have one home and it is the backend.
import {
  buildMergedOutputKey,
  isPhaseMerged,
  planMergeSlots
} from './mergeSlot.js';
import { groupByKeyOf, orderByBucket } from './groupBy.js';

const toArr = v => (Array.isArray(v) ? v : v == null ? [] : [v]);
const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * Every execution of a phase, flat, whichever shape the phase has.
 *
 * Mirrors the launch's own enumeration (`executionsOf` inside
 * `jobHelpers.buildJobFromProduct`) down to the precedence: a flat `execution`
 * array WINS over `conductors[]`, even when it is empty. Previewing the other
 * order would show drums the launch is not going to make.
 *
 * The colour comes from `conductor.isolation.color` and from nowhere else —
 * again because that is what the launch reads. A conductor carrying a colour
 * only at its top level reads as colourless here because it reads as colourless
 * THERE, and seeing that in the preview is the point: colourless executions all
 * share one merge key and one group-by bucket.
 */
export function executionsOf(phase) {
  if (!phase) return [];

  if (Array.isArray(phase.execution)) {
    return phase.execution.map(exec => ({
      exec,
      color: exec?.color ?? phase?.color ?? null,
      conductor: null
    }));
  }

  if (Array.isArray(phase.conductors)) {
    return phase.conductors.flatMap(c =>
      (c?.execution || []).map(exec => ({
        exec,
        color: c?.isolation?.color ?? c?.color ?? null,
        conductor: c
      }))
    );
  }

  return [];
}

/** The wizard's id for a product — the same identity the launch uses. */
export function productIdentityOf(product, index = 0) {
  return String(
    product?.launchInstanceId ||
      product?._id ||
      product?.productLabel ||
      `product_${index}`
  );
}

/** What this execution puts on a drum: metres, or coils/rewinds multiplied out. */
export function executionLength(exec) {
  if (exec?.coilCount && exec?.coilLength) return num(exec.coilCount) * num(exec.coilLength);
  if (exec?.rewindCount && exec?.rewindLength) return num(exec.rewindCount) * num(exec.rewindLength);
  return num(exec?.plannedLength) || num(exec?.fromUpstreamLength);
}

/**
 * The material lines the operator panel will print in the Input column.
 *
 * The field names are the backend's (`material`, `wireType`, `wireDiameter`,
 * `numberOfWires`, `numberOfDrums`, `grade`), so the panel's own formatting
 * rules apply unchanged to a previewed row and to a queued one.
 *
 * Only the lines are mirrored, not the weights: `planned.quantity_kg` is
 * computed in one place for the whole factory and this is not it.
 */
export function previewMaterialPlan({ product, phase, exec, conductor } = {}) {
  const name = phase?.name;
  const rows = [];
  if (!name) return rows;

  if (name === 'unwinding') {
    // A pay-off drum is one wire; its Ø is on the execution, which is also what
    // a merged unwinding pairs by.
    const d = exec?.wireDiameter ?? phase?.wireDiameter;
    if (d) {
      rows.push({ material: 'COPPER', form: 'WIRE', wireType: 'SINGLE-WIRE', wireDiameter: num(d) });
    }
    return rows;
  }

  if (name === 'pre_stranding') {
    // Two shapes: `rope[]` per spec (a mixed-section cable has two entries), or
    // the fields flat on the phase.
    const entries =
      Array.isArray(phase.rope) && phase.rope.length
        ? phase.rope
        : phase.wireDiameter != null
          ? [phase]
          : [];

    for (const r of entries) {
      const counts = toArr(r.numberOfWires);
      const drums = num(r.numberOfDrums) || 1;
      toArr(r.wireDiameter).forEach((d, i) => {
        const wires = num(counts[i]);
        if (!d || !wires) return;
        rows.push({
          material: r.material || 'COPPER',
          form: 'WIRE',
          wireType: String(r.type || 'MULTI-WIRE').toUpperCase(),
          wireDiameter: num(d),
          numberOfWires: wires * drums,
          numberOfDrums: drums
        });
      });
    }
    return rows;
  }

  if (name === 'conductor_isolation') {
    const wire = conductor?.wire;
    // Copper only when no pre_stranding feeds this phase — otherwise the copper
    // is already counted there, and the panel shows the rope token instead.
    const hasPreStranding = (product?.phases || []).some(p => p?.name === 'pre_stranding');
    const wireType = String(wire?.type || '').trim().toUpperCase();

    if (wire && !hasPreStranding) {
      if (wireType === 'SINGLE-WIRE' && wire.diameter) {
        rows.push({
          material: 'COPPER',
          form: 'WIRE',
          wireType: 'SINGLE-WIRE',
          wireDiameter: num(wire.diameter)
        });
      }
      if (wireType === 'MULTI-WIRE' || wireType === 'STRANDED') {
        const counts = toArr(wire.numberOfWires);
        toArr(wire.wireDiameter).forEach((d, i) => {
          const wires = num(counts[i]);
          if (!d || !wires) return;
          rows.push({
            material: 'COPPER',
            form: 'WIRE',
            wireType,
            wireDiameter: num(d),
            numberOfWires: wires
          });
        });
      }
    }

    const iso = conductor?.isolation;
    if (iso?.material) {
      rows.push({
        material: iso.material,
        form: 'COMPOUND',
        grade: iso.type || null,
        color: iso.color || null,
        layer: name
      });
    }
    return rows;
  }

  if (name === 'interior_sheat' || name === 'exterior_sheat') {
    if (phase?.material || phase?.fillingSection) {
      rows.push({
        material: phase.material || 'PVC',
        form: 'COMPOUND',
        grade: phase.type || null,
        layer: name
      });
    }
    return rows;
  }

  if (name === 'armouring') {
    const spec = [phase?.wide, phase?.thickness].filter(Boolean).join(' x ');
    rows.push({
      material: phase?.material || 'BLR',
      form: 'TAPE',
      grade: spec || phase?.type || null,
      layer: name
    });
    return rows;
  }

  // `rope` has no material of its own — it consumes the isolated conductors,
  // which the panel shows as input tokens.
  return rows;
}

/**
 * The wire Ø a group-by-diameter run buckets on.
 *
 * The launch reads `materialPlan[0].wireDiameter`, so the preview reads the
 * first line of the plan it just derived — same field, same position.
 */
function groupWireDiameter(materialPlan) {
  return materialPlan?.[0]?.wireDiameter ?? null;
}

/**
 * The queue groups a launch of these products would produce for ONE phase.
 *
 * @param standardData  the wizard's products
 * @param mergedPhases  { [phaseName]: true } — which phases put several products on one drum
 * @param groupByPhases { [phaseName]: 'color' | 'wireDiameter' } — which phases run grouped
 * @param phaseName     the phase being previewed
 * @param labelOf       (product) => display label
 * @returns the `groups` shape of GET /queues/:machineId, for buildBlockRows
 */
export function buildLaunchPreviewGroups({
  standardData = [],
  mergedPhases = {},
  groupByPhases = {},
  phaseName,
  labelOf = p => p?.productLabel || p?.name || '—'
} = {}) {
  const groups = [];
  let seq = 0;

  (standardData || []).forEach((product, productIndex) => {
    const phase = (product?.phases || []).find(p => p?.name === phaseName);
    if (!phase) return;

    const rows = executionsOf(phase);
    if (!rows.length) return;

    const identity = productIdentityOf(product, productIndex);
    const merged = isPhaseMerged(mergedPhases, phaseName);
    const groupMode = groupByPhases?.[phaseName] || null;

    // Per PRODUCT, exactly as the launch calls it (see jobHelpers' per-product
    // `mergeSlotPlan`): each product numbers its own executions, and two
    // products independently landing on slot 0 is what pairs them.
    const slots = merged
      ? planMergeSlots(
          rows.map(({ exec, color }, i) => ({
            id: `${phaseName}#${i}`,
            jobKey: identity,
            baseKey: buildMergedOutputKey({ phaseName, exec: { ...exec, color } }),
            mergeSlot: exec?.mergeSlot,
            mergeSolo: !!exec?.mergeSolo
          }))
        )
      : new Map();

    const queue = rows.map(({ exec, color, conductor }, i) => {
      const taskId = `${identity}#${phaseName}#${i}`;
      const materialPlan = Array.isArray(exec?.materialPlan) && exec.materialPlan.length
        ? exec.materialPlan
        : previewMaterialPlan({ product, phase, exec, conductor });

      // Merged executions share this; everything else gets its own. This is the
      // whole mechanism — `sameSharedExecution` collapses adjacent entries that
      // write to one token.
      let tokenId = `PREVIEW-${taskId}`;
      let mergeQueueKey = null;
      if (merged) {
        const base = buildMergedOutputKey({ phaseName, exec: { ...exec, color } });
        mergeQueueKey = `${base}::slot_${slots.get(`${phaseName}#${i}`) ?? 0}`;
        tokenId = mergeQueueKey;
      }

      // A merged drum buckets by its shared token and never by colour — the
      // launch does the same, because the merge is a physical fact and the
      // grouping only a preference.
      const groupByKey = mergeQueueKey
        ? null
        : groupByKeyOf({
            phaseName,
            mode: groupMode,
            color,
            wireDiameter: groupWireDiameter(materialPlan)
          });

      const plannedLength = executionLength(exec);

      return {
        taskId,
        phase: phaseName,
        status: 'PENDING',
        machineId: exec?.machineId || null,
        color,
        plannedLength,
        executedLength: 0,
        progress: 0,
        deliveryMode: exec?.deliveryMode || null,
        coilCount: exec?.coilCount ?? null,
        coilLength: exec?.coilLength ?? null,
        rewindCount: exec?.rewindCount ?? null,
        rewindLength: exec?.rewindLength ?? null,
        drum_material: exec?.drum_material || null,
        drum_diameter: exec?.drum_diameter || null,
        // Read by machineQueueBlocks to draw the run bracket and its heading.
        ...(groupByKey ? { groupByKey } : {}),
        // What the bucket ordering below needs, mirroring a pending enqueue.
        ...(mergeQueueKey ? { mergeQueueKey } : {}),
        // Nothing exists yet, so there are no input tokens to show — the
        // material lines stand in for them, as they do for a queued drum whose
        // upstream has not been made.
        inputs: [],
        materialPlan,
        outputs: [
          {
            tokenId,
            phase: phaseName,
            status: 'PENDING',
            plannedLength,
            producedLength: 0,
            availableLength: 0,
            consumedLength: 0,
            progress: 0,
            color,
            drum_material: exec?.drum_material || null,
            drum_diameter: exec?.drum_diameter || null,
            deliveryMode: exec?.deliveryMode || null,
            coilCount: exec?.coilCount ?? null,
            coilLength: exec?.coilLength ?? null
          }
        ],
        // The order the machine will work in. Products interleave on a merged or
        // grouped phase, so this is assigned across all of them, not per product.
        _queueSeq: seq++
      };
    });

    groups.push({
      jobId: identity,
      productLabel: labelOf(product),
      name: product?.name || null,
      numberOfConductors: product?.numberOfConductors ?? null,
      section: product?.section ?? null,
      greenYellowSection: product?.greenYellowSection ?? null,
      plannedLength: Number(product?.length) || 0,
      allocations: [],
      queue
    });
  });

  return interleaveForMerge(groups);
}

/**
 * Re-stamp `_queueSeq` in the order the launch will enqueue.
 *
 * Numbered product by product, the two halves of a merged drum sit far apart and
 * `buildDisplayBlocks` — which merges only ADJACENT entries, deliberately —
 * shows them as two separate drums; a group-by run comes out as one product
 * after the other rather than colour by colour.
 *
 * The order itself is not decided here: `orderByBucket` is the launch's own
 * rule, so the preview cannot invent a sequence the machine will not follow.
 */
export function interleaveForMerge(groups = []) {
  const flat = [];
  for (const g of groups) {
    for (const task of g.queue || []) flat.push(task);
  }

  orderByBucket(flat).forEach((task, i) => {
    task._queueSeq = i;
  });

  return groups;
}

export default buildLaunchPreviewGroups;
